import React, { useMemo } from 'react';

import { PaginationItem } from './PaginationItem';

import { Container } from './styles';

interface PaginationProps {
  onPageChange: (page: number) => void;
  resultsPerPage?: number;
  pageIndex: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const siblingsCount = 1;

function generatePagesArray(from: number, to: number) {
  return [...new Array(to - from)]
    .map((_, index) => {
      return from + index + 1;
    })
    .filter(page => page > 0);
}

export function Pagination({
  onPageChange,
  pageIndex,
  totalPages,
  hasPreviousPage,
  totalCount,
  resultsPerPage,
  hasNextPage,
}: PaginationProps) {
  const previousPages =
    pageIndex > 1
      ? generatePagesArray(pageIndex - 1 - siblingsCount, pageIndex - 1)
      : [];

  const nextPages =
    pageIndex < totalPages
      ? generatePagesArray(
          pageIndex,
          Math.min(pageIndex + siblingsCount, totalPages),
        )
      : [];

  const visualizationPagesStart = useMemo(() => {
    return resultsPerPage * (pageIndex - 1) + 1;
  }, [pageIndex, resultsPerPage]);

  const visualizationPagesEnd = useMemo(() => {
    /* istanbul ignore next */
    if (totalCount <= resultsPerPage) return totalCount;
    return resultsPerPage * (pageIndex - 1) + resultsPerPage;
  }, [resultsPerPage, pageIndex, totalCount]);

  return (
    <Container className="pagination">
      <div className="pagination-info">
        <strong>{visualizationPagesStart}</strong> -{' '}
        <strong>{visualizationPagesEnd}</strong> de{' '}
        <strong>{totalCount}</strong>
      </div>

      <div className="pagination-controllers">
        {hasPreviousPage ? (
          <button
            type="button"
            onClick={() => onPageChange(pageIndex - 1)}
            className="arrows"
          >
            <span className="chevron-icon left gray" title="Voltar" />
          </button>
        ) : (
          <button type="button" disabled className="arrows">
            <span className="chevron-icon left gray" title="Voltar" />
          </button>
        )}

        {pageIndex > 1 + siblingsCount && (
          <>
            <PaginationItem onPageChange={onPageChange} number={1} />
            {pageIndex > 2 + siblingsCount && <p>...</p>}
          </>
        )}

        {previousPages.length > 0 &&
          previousPages.map(page => {
            return (
              <PaginationItem
                key={page}
                onPageChange={onPageChange}
                number={page}
              />
            );
          })}

        <PaginationItem
          onPageChange={onPageChange}
          number={pageIndex}
          isCurrent
        />

        {nextPages.length > 0 &&
          nextPages.map(page => {
            return (
              <PaginationItem
                key={page}
                onPageChange={onPageChange}
                number={page}
              />
            );
          })}

        {pageIndex + siblingsCount < totalPages && (
          <>
            {pageIndex + 1 + siblingsCount < totalPages && <p>...</p>}
            <PaginationItem onPageChange={onPageChange} number={totalPages} />
          </>
        )}

        {hasNextPage ? (
          <button
            type="button"
            onClick={() => onPageChange(pageIndex + 1)}
            className="arrows"
          >
            <span className="chevron-icon right gray" title="Próximo" />
          </button>
        ) : (
          <button type="button" disabled className="arrows">
            <span className="chevron-icon right gray" title="Próximo" />
          </button>
        )}
      </div>
    </Container>
  );
}
